import React, { useState, useEffect } from 'react';
import { X, Key } from 'lucide-react';
import { getEnvVar } from '../../config/env';

const API_BASE_URL = getEnvVar('VITE_FASTAPI_BASE_URL');

const PROVIDER_OPTIONS = [
  { provider_type: 'voice', provider_name: 'elevenlabs', label: 'ElevenLabs (Voice)' },
  { provider_type: 'voice', provider_name: 'retell', label: 'Retell (Voice & Phone)' },
  { provider_type: 'phone', provider_name: 'telnyx', label: 'Telnyx (Phone)' },
  { provider_type: 'llm', provider_name: 'openai', label: 'OpenAI (LLM)' },
  { provider_type: 'llm', provider_name: 'anthropic', label: 'Anthropic (LLM)' },
  { provider_type: 'llm', provider_name: 'google', label: 'Google (LLM)' },
];

interface ApiKeyOption {
  id: string;
  name: string;
  is_global: boolean;
}

interface WorkspaceAssignment {
  provider_type: string;
  provider_name: string;
  api_key_id: string;
}

interface WorkspaceApiKeyModalProps {
  workspaceId: string;
  workspaceName: string;
  isOpen: boolean;
  onClose: () => void;
  onSaved?: () => void;
}

export const WorkspaceApiKeyModal: React.FC<WorkspaceApiKeyModalProps> = ({
  workspaceId,
  workspaceName,
  isOpen,
  onClose,
  onSaved,
}) => {
  const [keysByProvider, setKeysByProvider] = useState<Record<string, ApiKeyOption[]>>({});
  const [assignments, setAssignments] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('admin_auth_token')}`,
    'Content-Type': 'application/json',
  });

  const getKey = (pt: string, pn: string) => `${pt}:${pn}`;

  useEffect(() => {
    if (isOpen && workspaceId) {
      loadAll();
    }
  }, [isOpen, workspaceId]);

  const loadAll = async () => {
    try {
      setLoading(true);
      setError(null);
      const [assignRes, keysRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/v1/admin/workspaces/${workspaceId}/provider-keys`, { headers: getAuthHeaders() }),
        fetch(`${API_BASE_URL}/api/v1/admin/api-keys`, { headers: getAuthHeaders() }),
      ]);
      if (!assignRes.ok) throw new Error('Failed to load workspace keys');
      if (!keysRes.ok) throw new Error('Failed to load API keys');
      const assignData = await assignRes.json();
      const keysData = await keysRes.json();
      const allKeys: Array<{ provider_type: string; provider_name: string; id: string; name: string; is_global: boolean }> = keysData.data || [];
      const assignList: WorkspaceAssignment[] = assignData.data || [];

      const byProvider: Record<string, ApiKeyOption[]> = {};
      PROVIDER_OPTIONS.forEach((p) => {
        const key = getKey(p.provider_type, p.provider_name);
        byProvider[key] = allKeys
          .filter((k: { provider_type: string; provider_name: string }) => k.provider_type === p.provider_type && k.provider_name === p.provider_name)
          .map((k: { id: string; name: string; is_global: boolean }) => ({ id: k.id, name: k.name, is_global: k.is_global }));
      });

      const assignMap: Record<string, string> = {};
      assignList.forEach((a: WorkspaceAssignment) => {
        assignMap[getKey(a.provider_type, a.provider_name)] = a.api_key_id;
      });

      PROVIDER_OPTIONS.forEach((p) => {
        const key = getKey(p.provider_type, p.provider_name);
        if (!assignMap[key] && byProvider[key]?.length) {
          const globalKey = byProvider[key].find((k) => k.is_global);
          if (globalKey) assignMap[key] = globalKey.id;
        }
      });

      setKeysByProvider(byProvider);
      setAssignments(assignMap);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  const hasKeys = (pt: string, pn: string) => {
    const k = keysByProvider[getKey(pt, pn)] || [];
    return k.length > 0;
  };

  const getSelectedKeyId = (pt: string, pn: string) => assignments[getKey(pt, pn)];

  const getGlobalKeyId = (pt: string, pn: string) => {
    const k = keysByProvider[getKey(pt, pn)] || [];
    return k.find((x) => x.is_global)?.id;
  };

  const handleSelect = async (provider: typeof PROVIDER_OPTIONS[0], apiKeyId: string) => {
    const key = getKey(provider.provider_type, provider.provider_name);
    const globalId = getGlobalKeyId(provider.provider_type, provider.provider_name);

    if (apiKeyId === globalId || !apiKeyId) {
      try {
        setSaving(true);
        setError(null);
        const res = await fetch(
          `${API_BASE_URL}/api/v1/admin/workspaces/${workspaceId}/provider-keys/${provider.provider_type}/${provider.provider_name}`,
          { method: 'DELETE', headers: getAuthHeaders() }
        );
        if (!res.ok) throw new Error('Failed to remove override');
        setAssignments((s) => ({ ...s, [key]: globalId || '' }));
        setSuccess('Using global key');
        onSaved?.();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to update');
      } finally {
        setSaving(false);
      }
    } else {
      try {
        setSaving(true);
        setError(null);
        const res = await fetch(`${API_BASE_URL}/api/v1/admin/workspaces/${workspaceId}/provider-keys`, {
          method: 'PUT',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            provider_type: provider.provider_type,
            provider_name: provider.provider_name,
            api_key_id: apiKeyId,
          }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || data.error || 'Failed to save');
        setAssignments((s) => ({ ...s, [key]: apiKeyId }));
        setSuccess('Key updated');
        onSaved?.();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to save');
      } finally {
        setSaving(false);
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={onClose} />
        <div className="relative bg-white rounded-lg shadow-xl max-w-xl w-full p-6 max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Workspace API Keys</h3>
              <p className="text-sm text-gray-500 mt-1">{workspaceName}</p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="h-5 w-5" />
            </button>
          </div>

          <p className="text-sm text-gray-600 mb-4">
            Select which API key to use for each provider. Only keys from the API Keys Master are shown. Default is the global key.
          </p>

          {error && <div className="mb-4 p-3 rounded-md bg-red-50 text-red-700 text-sm">{error}</div>}
          {success && <div className="mb-4 p-3 rounded-md bg-green-50 text-green-700 text-sm">{success}</div>}

          {loading ? (
            <div className="py-8 text-center text-gray-500">Loading...</div>
          ) : (
            <div className="space-y-4">
              {PROVIDER_OPTIONS.map((provider) => {
                const key = getKey(provider.provider_type, provider.provider_name);
                const options = keysByProvider[key] || [];
                const selectedId = getSelectedKeyId(provider.provider_type, provider.provider_name);
                const globalId = getGlobalKeyId(provider.provider_type, provider.provider_name);

                return (
                  <div
                    key={key}
                    className="p-4 rounded-lg border border-gray-200 space-y-3"
                  >
                    <div className="flex items-center gap-2">
                      <Key className="h-4 w-4 text-gray-400" />
                      <span className="text-sm font-medium text-gray-900">{provider.label}</span>
                    </div>

                    {options.length === 0 ? (
                      <p className="text-sm text-gray-500">No keys in API Keys Master. Add keys there first.</p>
                    ) : (
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Select API Key</label>
                        <select
                          value={selectedId || globalId || ''}
                          onChange={(e) => handleSelect(provider, e.target.value)}
                          disabled={saving}
                          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm py-2"
                        >
                          {options.map((opt) => (
                            <option key={opt.id} value={opt.id}>
                              {opt.name} {opt.is_global ? '(Global)' : ''}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
